import { LookupYesNoType } from '@/api/dtos';
import { AppSettings } from '@/AppSettings';
import EConsentChecklist from '@/components/consent/EConsentChecklist';
import EConsentPage from '@/components/consent/EConsentPage';
import EConsentSignatureFields from '@/components/consent/EConsentSignatureFields';
import EConsentSignatureUpdater from '@/components/consent/EConsentSignatureUpdater';
import EConsentTrialDetails from '@/components/consent/EConsentTrialDetails';
import { servicesAustraliaConsentEConsentRegistration } from '@/components/consent/eConsentFormRegistry';
import EConsentForm from '@/components/forms/EConsentForm';
import EConsentContext, { EConsentUserType } from '@/contexts/EConsentContext';
import { Box, Button, styled, Typography } from '@mui/material';
import { GroupedField, GroupedFields, IFormLabel, ILookup, InstitutionContext, ToggleButtonGroup } from '@ngt/opms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useContext } from 'react';

const Paragraph = styled(Typography)(({ theme }) => ({ paddingBottom: theme.spacing(1.5) }));
const SectionHeading = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: { width: '80%' },
}));
const PicfButton = styled(Button)(({ theme }) => ({ padding: theme.spacing(1) }));

const fieldGrid = { size: { xs: 12, sm: 12, md: 5, lg: 4, xl: 4 } };
const containerGrid = { sx: { paddingBottom: 1.5, paddingTop: 1.5 } };

const consentLookups: ILookup[] = [
    {
        propertyName: 'participantAgreedToDisclosureOfInformation',
        items: [
            { id: LookupYesNoType.Yes, value: 'Yes' },
            { id: LookupYesNoType.No, value: 'No' },
        ],
    },
];

const consentLabels: IFormLabel[] = [
    {
        propertyPath: 'participantAgreedToDisclosureOfInformation',
        detailedLabel: undefined,
        label: 'I consent to the disclosure of my MBS and PBS Commonwealth health information provided by Services Australia to researchers for the purposes of the study.',
    },
    { propertyPath: 'patientName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'datePatientSignedConsent', detailedLabel: undefined, label: 'Date' },
    { propertyPath: 'investigatorName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'dateInvestigatorSignedConsent', detailedLabel: undefined, label: 'Date' },
];

const consentStatements = [
    'My MBS and PBS health information will be disclosed by Services Australia for the purpose of the study.',
    'The results of this research may be published in articles or journals.',
    'My name will never be disclosed by Services Australia, used in the study, or published.',
    'My participation in the study is completely voluntary.',
    'I can withdraw my consent to release my Commonwealth health information provided by Services Australia to the study at any time, and I do not have to provide a reason.',
    'I have completed the selection below regarding participation in this optional component of OPT-Pembro, as described in the Participant Information Sheet.',
    'I understand the information provided to me about the study, have been given the opportunity to ask questions, and am satisfied with the answers I received.',
];

const ServicesAustraliaConsentFields: FC = () => {
    const { userType } = useContext(EConsentContext);

    return (
        <EConsentForm
            requestTypeName={servicesAustraliaConsentEConsentRegistration.requestTypeName}
            disableHeading
            disablePaper
            labels={consentLabels}
            lookups={consentLookups}
            validateOn={ValidateOn.onChange}
            debug={AppSettings.debug}
            hideSubmit
            hideCancel
            submitAndReturnButtonText="Submit"
        >
            <EConsentSignatureUpdater />
            <GroupedFields ContainerProps={{ borderBottom: 'none !important', borderTop: 'none !important' }}>
                <SectionHeading>Consent to release your Services Australia information</SectionHeading>
                <GroupedField
                    propertyPath="participantAgreedToDisclosureOfInformation"
                    component={ToggleButtonGroup}
                    FieldGridProps={fieldGrid}
                    ContainerGridProps={containerGrid}
                    disabled={userType === EConsentUserType.Investigator}
                />
                <EConsentSignatureFields
                    investigatorStatement="I have explained the Services Australia consent, its purpose and implications to the participant and I believe they have understood that explanation."
                />
            </GroupedFields>
        </EConsentForm>
    );
};

const ServicesAustraliaConsentEConsentForm: FC = () => {
    const { principalInvestigator, picfDownloadUrl, picfVersion } = useContext(EConsentContext);
    const { data: institution } = useContext(InstitutionContext);

    return (
        <EConsentPage>
            <EConsentTrialDetails details={[
                { label: 'Short Name of Trial', value: 'BCT 2601 OPT-Pembro' },
                {
                    label: 'Full Name of Trial',
                    value: 'Optimizing adjuvant prescription of Pembrolizumab in patients with early-stage triple-negative breast cancer achieving pathologic complete response after standard neoadjuvant chemotherapy and pembrolizumab.',
                },
                { label: 'Principal Investigator', value: principalInvestigator },
                { label: 'Trial sponsor', value: 'Breast Cancer Trials' },
                { label: 'Site Name', value: institution?.name },
            ]} />

            <Paragraph>
                This form asks for your consent to release Medicare Benefits Schedule (MBS) and Pharmaceutical
                Benefits Scheme (PBS) Commonwealth health information provided by Services Australia to Breast
                Cancer Trials for the purpose of the BCT 2601 OPT-Pembro study.
            </Paragraph>
            <Paragraph>
                Please read the full Services Australia Participant Information and Consent Form and ask the trial
                team any questions before deciding whether to provide this consent.
            </Paragraph>

            {picfVersion && picfDownloadUrl && (
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                    <PicfButton
                        color="primary"
                        variant="contained"
                        // @ts-expect-error MUI supports rendering Button as an anchor.
                        component="a"
                        href={picfDownloadUrl}
                        target="_blank"
                        rel="noopener"
                    >
                        {picfVersion}
                    </PicfButton>
                </Box>
            )}

            <SectionHeading>I understand:</SectionHeading>
            <EConsentChecklist items={consentStatements} />
            <ServicesAustraliaConsentFields />
        </EConsentPage>
    );
};

export default ServicesAustraliaConsentEConsentForm;
